import type { Vec2 } from '../../geo/types';
import {
  columnShiftPixel,
  residualShiftGradient,
  unwarpFromPainted,
  verticalShiftGradient,
  type PainterResidualWarp,
  type VerticalShiftField,
} from '../../render/painterWarp';
import { curvatureAt, splineCurvature } from './shiftCurvature';

export { columnCurvatureAt, curvatureAt } from './shiftCurvature';

/** Painting-frame pixel corners, [min, max], of a box. */
export type PixelBox = readonly [Vec2, Vec2];

/**
 * Over the rendered pixels along a chord: a bound on the slope of a component of the residual shift,
 * |∇D|, the same over the part of the chord in the box the canvas can show (over the whole chord where
 * it misses the box), one on its second derivative along any direction, and one on the turn of its
 * slope the spline centres the chord passes add beyond that.
 */
export interface ChordBound {
  readonly slope: number;
  readonly shownSlope: number;
  readonly curvature: number;
  readonly kink: number;
}

export interface ShiftBound {
  along(p: Vec2, q: Vec2): ChordBound;
}

// The slope and curvature are sampled this far apart over the pixels the canvas can show.
export const GRID_PX = 50;
// Between samples the curvature of the committed row shift rises by up to 1.96 times, 2 px and more
// from a spline centre, in a cell that holds one or lies within a cell of one, and by under 1.04 times
// elsewhere. The committed column shift, down to its last fitted row, rises by up to 2.48 times, 2.2 px
// from a centre in a cell whose corners hold a twentieth of its largest curvature, and by under 1.03
// times elsewhere; the rugged one of shiftBound.vertical.test.ts by up to 1.67 and 1.03 times.
const NEAR_CENTRE_MARGIN = 2.2;
const COLUMN_NEAR_CENTRE_MARGIN = 2.6;
const MARGIN = 1.1;
// Along the last fitted row the column spline's curvature is sampled this often; 2 px and more from a
// centre on the row it rises between samples by under 3%. Below the row the held shift's curvature,
// its second derivative along the row, is a diagonal entry of the spline's Hessian there, so no more.
const ROW_SAMPLE_PX = 0.5;
const ROW_MARGIN = 1.1;
// Nearer a centre its curvature grows like ln r past any bound, but the slope turns over the disc by
// under 4 |w| CENTRE_DISC_PX / scale² more than the cell bound allows.
const CENTRE_DISC_PX = 2;
// Beyond those pixels, 700 px and more from every centre, the curvature falls off smoothly, so one
// bound from a lattice this coarse, raised by OUTER_MARGIN, covers the rest of the reach.
const OUTER_PX = 250;
const OUTER_MARGIN = 1.5;
// A pixel lies within half a diagonal of a sample, or a pixel more where an edge bends off its chord,
// and the slope there differs from the sample's by at most the curvature times that.
const CELL_REACH_PX = (GRID_PX * Math.SQRT2) / 2 + 1;
const OUTER_REACH_PX = (OUTER_PX * Math.SQRT2) / 2 + 1;
// The canvas edges are unwarped on rows this far apart, and under a column shift its top and bottom
// on columns too; between them the unwarped edge moves under 0.21 px a row and under 0.13 px a column,
// for the committed warp and the column shifts of shiftBound.vertical.test.ts, so the margin covers it.
const EDGE_ROW_PX = 10;
const EDGE_MARGIN_PX = 2;
// Beyond the reach the shader holds the pixel, which kinks the shift where no curvature bounds it.
const UNBOUNDED: ChordBound = {
  slope: Infinity,
  shownSlope: Infinity,
  curvature: Infinity,
  kink: Infinity,
};
const FLAT: ChordBound = { slope: 0, shownSlope: 0, curvature: 0, kink: 0 };

/*
 * A component of the shift as the bound samples it: its slope and curvature at a rendered pixel, the
 * pixel whose value it takes there, the spline centres where its curvature grows past any bound, of
 * those the ones whose discs the cells carry, how far each turns its slope over the disc around it
 * beyond what the cell bound allows, how far its curvature rises between samples near a centre, and
 * the turns of slope a chord meets that no cell carries.
 */
interface BoundField {
  readonly slopeAt: (u: number, v: number) => number;
  readonly curvatureAt: (u: number, v: number) => number;
  readonly sampledAt: (u: number, v: number) => Vec2;
  readonly centresPx: readonly Vec2[];
  readonly discCentres: readonly number[];
  readonly kinkOf: (i: number) => number;
  readonly nearCentreMargin: number;
  readonly kinkAlong: (p: Vec2, q: Vec2) => number;
  readonly heldBelow: number;
  readonly heldCurvature: (u0: number, u1: number) => number;
}

interface Grid {
  readonly box: PixelBox;
  readonly cols: number;
  readonly rows: number;
  readonly slopes: Float64Array;
  readonly curvatures: Float64Array;
  // The spline centres whose discs reach into each cell, by cell.
  readonly discs: ReadonlyMap<number, readonly number[]>;
}

type CellBound = Pick<ChordBound, 'slope' | 'curvature'>;

const NO_DISCS: readonly number[] = [];

function rowField(warp: PainterResidualWarp): BoundField {
  return {
    slopeAt: (u, v) => Math.hypot(...residualShiftGradient(warp, [u, v])),
    curvatureAt: (u, v) => curvatureAt(warp, u, v),
    sampledAt: (u, v) => [u, v],
    centresPx: warp.centresPx,
    discCentres: warp.centresPx.map((_, i) => i),
    kinkOf: (i) => (4 * Math.abs(warp.weights[i]!) * CENTRE_DISC_PX) / warp.scalePx ** 2,
    nearCentreMargin: NEAR_CENTRE_MARGIN,
    kinkAlong: () => 0,
    heldBelow: Infinity,
    heldCurvature: () => 0,
  };
}

// The pieces of the chord from p to q as the column shift takes it: the part above the last fitted
// row as it lies, and the part below moved up onto that row.
function heldPieces(p: Vec2, q: Vec2, last: number): (readonly [Vec2, Vec2])[] {
  const onRow = (a: Vec2): Vec2 => [a[0], Math.min(a[1], last)];
  if ((p[1] - last) * (q[1] - last) >= 0) return [[onRow(p), onRow(q)]];
  const t = (last - p[1]) / (q[1] - p[1]);
  const crossing: Vec2 = [p[0] + t * (q[0] - p[0]), last];
  return [
    [onRow(p), crossing],
    [crossing, onRow(q)],
  ];
}

/*
 * The turns of the column shift's slope along a chord that its cells do not carry. Where the chord
 * crosses the last fitted row the slope down the column stops, a turn of the spline's slope down the
 * column there times the chord's share down the columns. A centre on that row, or within its disc of
 * it, repeats its ln r down the column beneath it, so its disc's turn counts wherever the chord as
 * the shift takes it passes the disc.
 */
function columnKinkAlong(
  warp: PainterResidualWarp,
  columns: VerticalShiftField,
  rowCentres: readonly number[],
  kinkOf: (i: number) => number
): (p: Vec2, q: Vec2) => number {
  const last = columns.lastFittedRowPx;
  return (p, q) => {
    const pieces = heldPieces(p, q, last);
    const crossing = pieces.length > 1 ? pieces[0]![1] : null;
    const down = Math.abs(q[1] - p[1]) / Math.hypot(q[0] - p[0], q[1] - p[1]);
    const stop = crossing ? Math.abs(verticalShiftGradient(warp, crossing)[1]) * down : 0;
    return rowCentres.reduce((kink, i) => {
      const c = columns.centresPx[i]!;
      const passes = pieces.some(([a, b]) => distanceToSegment(c, a, b) < CENTRE_DISC_PX);
      return passes ? kink + kinkOf(i) : kink;
    }, stop);
  };
}

/*
 * Below the last fitted row the column shift is that row's, whose curvature the spline's on the row
 * bounds; it is sampled along the row every ROW_SAMPLE_PX over the reach, leaving out the discs of the
 * centres on the row, whose turns count instead, and between samples it rises by under ROW_MARGIN.
 */
function rowCurvature(
  columns: VerticalShiftField,
  rowCentres: readonly Vec2[],
  reach: PixelBox
): (u0: number, u1: number) => number {
  const last = columns.lastFittedRowPx;
  const start = reach[0][0];
  const count = Math.ceil((reach[1][0] - start) / ROW_SAMPLE_PX) + 1;
  const inDisc = (u: number) =>
    rowCentres.some(([cu, cv]) => Math.hypot(u - cu, last - cv) < CENTRE_DISC_PX);
  const samples = Float64Array.from({ length: count }, (_, i) => {
    const u = start + i * ROW_SAMPLE_PX;
    return inDisc(u) ? 0 : splineCurvature(columns, u, last);
  });
  return (u0, u1) => {
    const first = Math.max(0, Math.floor((u0 - start) / ROW_SAMPLE_PX) - 1);
    const past = Math.min(count, Math.ceil((u1 - start) / ROW_SAMPLE_PX) + 2);
    let largest = 0;
    for (let i = first; i < past; i++) largest = Math.max(largest, samples[i]!);
    return largest * ROW_MARGIN;
  };
}

/*
 * Near a centre the column shift's curvature grows as the row shift's does, times the lever, which
 * changes by at most CENTRE_DISC_PX / s over the disc. The cells sample the spline itself, down to
 * the last fitted row and on that row below it, whose slope and curvature bound the held shift's.
 */
function columnField(
  warp: PainterResidualWarp,
  columns: VerticalShiftField,
  reach: PixelBox
): BoundField {
  const s = columns.scalePx;
  const last = columns.lastFittedRowPx;
  const kinkOf = (i: number) => {
    const lever = Math.abs(columns.centresPx[i]![1] - columns.heldRowPx) / s + CENTRE_DISC_PX / s;
    return (lever * 4 * Math.abs(columns.weights[i]!) * CENTRE_DISC_PX) / s ** 2;
  };
  const indices = columns.centresPx.map((_, i) => i);
  const onRow = (i: number) => columns.centresPx[i]![1] > last - CENTRE_DISC_PX;
  const sampledAt = (u: number, v: number) => columnShiftPixel(columns, [u, v]);
  return {
    slopeAt: (u, v) => Math.hypot(...verticalShiftGradient(warp, sampledAt(u, v))),
    curvatureAt: (u, v) => splineCurvature(columns, u, Math.min(v, last)),
    sampledAt,
    centresPx: columns.centresPx,
    discCentres: indices.filter((i) => !onRow(i)),
    kinkOf,
    nearCentreMargin: COLUMN_NEAR_CENTRE_MARGIN,
    kinkAlong: columnKinkAlong(warp, columns, indices.filter(onRow), kinkOf),
    heldBelow: last,
    heldCurvature: rowCurvature(
      columns,
      indices.filter(onRow).map((i) => columns.centresPx[i]!),
      reach
    ),
  };
}

function inBox([u, v]: Vec2, [min, max]: PixelBox): boolean {
  return u >= min[0] && u <= max[0] && v >= min[1] && v <= max[1];
}

// The unwarped ends of the rows, or of the columns, of a box, EDGE_ROW_PX apart.
function unwarpedEnds(warp: PainterResidualWarp, [min, max]: PixelBox, axis: 0 | 1): Vec2[] {
  const across = 1 - axis;
  const count = Math.ceil((max[axis] - min[axis]) / EDGE_ROW_PX);
  return Array.from({ length: count + 1 }, (_, i) => {
    const at = Math.min(min[axis] + i * EDGE_ROW_PX, max[axis]);
    const end = (edge: number): Vec2 => (axis === 1 ? [edge, at] : [at, edge]);
    return [unwarpFromPainted(warp, end(min[across]!)), unwarpFromPainted(warp, end(max[across]!))];
  }).flat();
}

/**
 * The box of rendered pixels the shift draws into the `painted` box. Over every pixel it draws into
 * the canvas the warp's Jacobian determinant stays above 0.5; the committed column shift folds only
 * sky 800 px and more above the canvas, and draws none of it into the canvas. So the pixels drawn
 * into the box lie within the unwarped image of its outline. Without a column shift each pixel moves
 * along its row, and 1 + dD/du of the committed row shift stays above 0.59 over the reach, so those
 * drawn into a row of the box run between the unwarped ends of that row and the box keeps its rows;
 * a column shift bends its top and bottom edges as well.
 */
export function renderedBoxOf(warp: PainterResidualWarp, box: PixelBox): PixelBox {
  const [min, max] = box;
  const sides = unwarpedEnds(warp, box, 1);
  if (!warp.vertical) {
    return [
      [Math.min(...sides.map(([u]) => u)) - EDGE_MARGIN_PX, min[1]],
      [Math.max(...sides.map(([u]) => u)) + EDGE_MARGIN_PX, max[1]],
    ];
  }
  const outline = [...sides, ...unwarpedEnds(warp, box, 0)];
  const extent = (axis: 0 | 1, pick: (...values: number[]) => number) =>
    pick(...outline.map((p) => p[axis]));
  return [
    [extent(0, Math.min) - EDGE_MARGIN_PX, extent(1, Math.min) - EDGE_MARGIN_PX],
    [extent(0, Math.max) + EDGE_MARGIN_PX, extent(1, Math.max) + EDGE_MARGIN_PX],
  ];
}

function nearCentre(centresPx: readonly Vec2[], u: number, v: number): boolean {
  return centresPx.some(
    ([cu, cv]) =>
      Math.max(u - cu, cu - u - GRID_PX, 0) < GRID_PX &&
      Math.max(v - cv, cv - v - GRID_PX, 0) < GRID_PX
  );
}

// Every centre lies among the pixels the canvas shows, so every disc lies in cells of the grid.
function discCells(field: BoundField, min: Vec2, cols: number, rows: number) {
  const discs = new Map<number, number[]>();
  const cellOf = (x: number, lo: number, count: number) =>
    Math.min(count - 1, Math.max(0, Math.floor((x - lo) / GRID_PX)));
  field.discCentres.forEach((i) => {
    const [u, v] = field.centresPx[i]!;
    const [c0, c1] = [u - CENTRE_DISC_PX, u + CENTRE_DISC_PX].map((x) => cellOf(x, min[0], cols));
    const [r0, r1] = [v - CENTRE_DISC_PX, v + CENTRE_DISC_PX].map((y) => cellOf(y, min[1], rows));
    for (let row = r0!; row <= r1!; row++) {
      for (let col = c0!; col <= c1!; col++)
        discs.set(row * cols + col, [...(discs.get(row * cols + col) ?? []), i]);
    }
  });
  return discs;
}

function gridOf(field: BoundField, [min, max]: PixelBox): Grid {
  const cols = Math.ceil((max[0] - min[0]) / GRID_PX);
  const rows = Math.ceil((max[1] - min[1]) / GRID_PX);
  const nodeAt = (n: number): Vec2 => [
    min[0] + (n % (cols + 1)) * GRID_PX,
    min[1] + Math.floor(n / (cols + 1)) * GRID_PX,
  ];
  const nodes = Array.from({ length: (cols + 1) * (rows + 1) }, (_, n) => nodeAt(n));
  const nodeSlopes = nodes.map(([u, v]) => field.slopeAt(u, v));
  const nodeCurvatures = nodes.map(([u, v]) => field.curvatureAt(u, v));
  const cornersOf = (c: number) => {
    const n = Math.floor(c / cols) * (cols + 1) + (c % cols);
    return [n, n + 1, n + cols + 1, n + cols + 2];
  };
  const curvatures = Float64Array.from({ length: cols * rows }, (_, c) => {
    const [u, v] = nodes[cornersOf(c)[0]!]!;
    const margin = nearCentre(field.centresPx, ...field.sampledAt(u, v))
      ? field.nearCentreMargin
      : MARGIN;
    const corners = Math.max(...cornersOf(c).map((n) => nodeCurvatures[n]!)) * margin;
    if (v + GRID_PX <= field.heldBelow) return corners;
    return Math.max(corners, field.heldCurvature(u, u + GRID_PX));
  });
  const slopes = Float64Array.from(
    { length: cols * rows },
    (_, c) => Math.max(...cornersOf(c).map((n) => nodeSlopes[n]!)) + curvatures[c]! * CELL_REACH_PX
  );
  return {
    box: [min, [min[0] + cols * GRID_PX, min[1] + rows * GRID_PX]],
    cols,
    rows,
    slopes,
    curvatures,
    discs: discCells(field, min, cols, rows),
  };
}

// Lattice points deeper inside the grid than a lattice step are left out, so every pixel beyond the
// grid still lies within half a lattice diagonal of one.
function outerBound(field: BoundField, [min, max]: PixelBox, reach: PixelBox): CellBound {
  const deep: PixelBox = [
    [min[0] + OUTER_PX, min[1] + OUTER_PX],
    [max[0] - OUTER_PX, max[1] - OUTER_PX],
  ];
  let [slope, curvature] = [0, 0];
  for (let v = reach[0][1]; v <= reach[1][1]; v += OUTER_PX) {
    for (let u = reach[0][0]; u <= reach[1][0]; u += OUTER_PX) {
      if (inBox([u, v], deep)) continue;
      slope = Math.max(slope, field.slopeAt(u, v));
      curvature = Math.max(curvature, field.curvatureAt(u, v));
    }
  }
  const held = reach[1][1] > field.heldBelow ? field.heldCurvature(reach[0][0], reach[1][0]) : 0;
  const bound = Math.max(curvature * OUTER_MARGIN, held);
  return { slope: slope + bound * OUTER_REACH_PX, curvature: bound };
}

// The part of the chord from p to q inside the box, as parameters along it (Liang and Barsky).
function clipped(p: Vec2, q: Vec2, [min, max]: PixelBox): readonly [number, number] | null {
  let [t0, t1] = [0, 1];
  for (const axis of [0, 1] as const) {
    const d = q[axis] - p[axis];
    if (d === 0) {
      if (p[axis] < min[axis] || p[axis] > max[axis]) return null;
      continue;
    }
    const [ta, tb] = [(min[axis] - p[axis]) / d, (max[axis] - p[axis]) / d];
    t0 = Math.max(t0, Math.min(ta, tb));
    t1 = Math.min(t1, Math.max(ta, tb));
  }
  return t0 <= t1 ? [t0, t1] : null;
}

// Calls `visit` with every cell the segment from a to b crosses, stepping from cell to cell where it
// crosses a column or a row (Amanatides and Woo).
function forCellsCrossed(grid: Grid, a: Vec2, b: Vec2, visit: (cell: number) => void): void {
  const cellOf = (x: number, lo: number, count: number) =>
    Math.min(count - 1, Math.max(0, Math.floor((x - lo) / GRID_PX)));
  const lo = grid.box[0];
  const [du, dv] = [b[0] - a[0], b[1] - a[1]];
  let col = cellOf(a[0], lo[0], grid.cols);
  let row = cellOf(a[1], lo[1], grid.rows);
  const [endCol, endRow] = [cellOf(b[0], lo[0], grid.cols), cellOf(b[1], lo[1], grid.rows)];
  const [stepU, stepV] = [Math.sign(du), Math.sign(dv)];
  const [dtU, dtV] = [GRID_PX / Math.abs(du), GRID_PX / Math.abs(dv)];
  let tU = du === 0 ? Infinity : (lo[0] + (col + (stepU > 0 ? 1 : 0)) * GRID_PX - a[0]) / du;
  let tV = dv === 0 ? Infinity : (lo[1] + (row + (stepV > 0 ? 1 : 0)) * GRID_PX - a[1]) / dv;
  for (let n = 0; n <= grid.cols + grid.rows; n++) {
    visit(row * grid.cols + col);
    if (col === endCol && row === endRow) return;
    if (tU < tV) {
      col += stepU;
      tU += dtU;
    } else {
      row += stepV;
      tV += dtV;
    }
    if (col < 0 || col >= grid.cols || row < 0 || row >= grid.rows) return;
  }
}

function distanceToSegment(c: Vec2, p: Vec2, q: Vec2): number {
  const [du, dv] = [q[0] - p[0], q[1] - p[1]];
  const span = du * du + dv * dv;
  const t =
    span > 0 ? Math.max(0, Math.min(1, ((c[0] - p[0]) * du + (c[1] - p[1]) * dv) / span)) : 0;
  return Math.hypot(c[0] - p[0] - t * du, c[1] - p[1] - t * dv);
}

// The largest bounds of the cells the chord from p to q crosses between parameters t0 and t1, and the
// turns the discs it passes through add.
function crossedBound(
  grid: Grid,
  field: BoundField,
  [p, q]: readonly [Vec2, Vec2],
  [t0, t1]: readonly [number, number]
): Omit<ChordBound, 'shownSlope'> {
  const at = (t: number): Vec2 => [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
  let [slope, curvature, kink] = [0, 0, 0];
  let passed: number[] | null = null;
  forCellsCrossed(grid, at(t0), at(t1), (cell) => {
    slope = Math.max(slope, grid.slopes[cell]!);
    curvature = Math.max(curvature, grid.curvatures[cell]!);
    for (const i of grid.discs.get(cell) ?? NO_DISCS) {
      if (passed?.includes(i) || distanceToSegment(field.centresPx[i]!, p, q) >= CENTRE_DISC_PX) {
        continue;
      }
      (passed ??= []).push(i);
      kink += field.kinkOf(i);
    }
  });
  return { slope, curvature, kink };
}

function boundOver(field: BoundField, box: PixelBox, reach: PixelBox): ShiftBound {
  const grid = gridOf(field, box);
  const outer = outerBound(field, grid.box, reach);
  return {
    along(p, q) {
      if (!inBox(p, reach) || !inBox(q, reach)) return UNBOUNDED;
      const span = clipped(p, q, grid.box);
      const cells = span ? crossedBound(grid, field, [p, q], span) : { ...outer, kink: 0 };
      const kink = cells.kink + field.kinkAlong(p, q);
      if (span && span[0] === 0 && span[1] === 1)
        return { ...cells, shownSlope: cells.slope, kink };
      return {
        slope: Math.max(cells.slope, outer.slope),
        shownSlope: cells.slope,
        curvature: Math.max(cells.curvature, outer.curvature),
        kink,
      };
    },
  };
}

/**
 * Bounds on the row shift's slope and curvature: cell by cell over `box`, the rendered pixels the
 * canvas can show, and one pair over the rest of `reach`, beyond which the shader holds the pixel.
 */
export function createShiftBound(
  warp: PainterResidualWarp,
  box: PixelBox,
  reach: PixelBox
): ShiftBound {
  return boundOver(rowField(warp), box, reach);
}

/** The same bounds on the column shift, which a warp without one keeps flat everywhere. */
export function createColumnShiftBound(
  warp: PainterResidualWarp,
  box: PixelBox,
  reach: PixelBox
): ShiftBound {
  const columns = warp.vertical;
  if (!columns) return { along: () => FLAT };
  return boundOver(columnField(warp, columns, reach), box, reach);
}
